<script>
  import rules from '@/helpers/validation-rules';
  import 'firebase/auth';
  import SignUpCredentials from '@/components/sections/SignUpCredentials.vue';
  import { SIGN_UP_STEPS } from '@/helpers/enums';
  import SignupPersonalInfo from '@/components/sections/SignupPersonalInfo.vue';
  import StatesList from '@/data/us-states';
  import CountryList from '@/data/countryData';
  import SignUpEmailVerification from '@/components/sections/SignUpEmailVerification.vue';
  import SignUpVerifyPhone from '@/components/sections/SignUpVerifyPhone.vue';
  import SignUpOTPVerification from '@/components/sections/SignUpOTPVerification.vue';
  import SignUpCongrats from '@/components/sections/SignUpCongrats.vue';

  const MANDATORY_STEPS = [SIGN_UP_STEPS.VERIFY_PHONE, SIGN_UP_STEPS.OTP_VERIFICATION];

  export default {
    components: {
      SignUpCongrats,
      SignUpOTPVerification,
      SignUpVerifyPhone,
      SignUpEmailVerification,
      SignupPersonalInfo,
      SignUpCredentials,
    },

    props: {
      value: Boolean,
    },

    data: () => ({
      rules,
      isVisible: false,
      MANDATORY_STEPS,

      regFormOR: require('@/assets/images/svg/reg-form-or.svg'),

      model: {
        uid: '',
        firstName: '',
        lastname: '',
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        displayName: '',
        phoneNumber: '',
        birthdate: {
          day: null,
          month: null,
          year: null,
        },
        phone: {
          countryCode: '+1',
          number: '',
        },
        address: '',
        city: '',
        state: '',
        country: 'USA',
        terms: false,
        collection: false,
        marketing: true,
      },

      statesList: StatesList,
      countryList: CountryList,

      emailSuggestion: '',

      monthList: [
        { text: 'January', value: 1 },
        { text: 'February', value: 2 },
        { text: 'March', value: 3 },
        { text: 'April', value: 4 },
        { text: 'May', value: 5 },
        { text: 'June', value: 6 },
        { text: 'July', value: 7 },
        { text: 'August', value: 8 },
        { text: 'September', value: 9 },
        { text: 'October', value: 10 },
        { text: 'November', value: 11 },
        { text: 'December', value: 12 },
      ],

      currentEmail: '',

      country: 'US',
      step: 2,
    }),

    computed: {
      isSocialSignUp() {
        return this.$store.state.isSocialSignUp;
      },

      SIGN_UP_STEPS() {
        return SIGN_UP_STEPS;
      },

      signupStep() {
        return this.$store.state.authData.signupStep;
      },

      roundedSignupStep() {
        return Math.floor(this.signupStep);
      },

      isPopupPersistent() {
        return this.MANDATORY_STEPS.includes(this.signupStep);
      },
    },

    watch: {
      value: {
        handler(newValue) {
          if (this.$store.state.playerInfo?.profile?.accountStatus === 'suspended') return;
          this.isVisible = newValue;
        },
        immediate: true,
      },
    },

    methods: {
      updateCredentials(userData) {
        if (!userData) return;

        const splitName = userData.displayName.split(' ');
        this.model.displayName = userData.displayName;
        this.model.email = userData.email;
        this.model.firstName = splitName[0] || '';
        this.model.lastname = splitName[1] || '';
      },

      closeModal(fromCloseButton = false) {
        if (!fromCloseButton && this.isPopupPersistent) {
          return;
        }

        this.$store.dispatch('changeSignupStep', SIGN_UP_STEPS.CREDENTIALS);
        this.$store.dispatch('changeSignupDialogState', false);
      },

      async logout() {
        await this.$store.dispatch('signOut');

        this.$store.dispatch('changeSignupStep', SIGN_UP_STEPS.REDIRECT_TO_ONE);
        this.$store.dispatch('changeSignupDialogState', false);

        this.$router.push('/');
      },
    },
  };
</script>

<template>
  <v-dialog
    v-if="value"
    v-model="isVisible"
    :fullscreen="$vuetify.breakpoint.xs"
    :persistent="isPopupPersistent"
    content-class="outlined-modal login-form ma-0"
    style="max-height: 100% !important"
    transition="dialog-top-transition"
    width="100%"
    @click:outside="closeModal(false)"
    @keydown.esc="closeModal(false)"
  >
    <v-card
      :class="{ small: signupStep === SIGN_UP_STEPS.PERSONAL_INFO, confetti: signupStep === SIGN_UP_STEPS.CONGRATS }"
      class="outlined-modal-card d-flex align-center flex-column"
      data-state="open"
      max-width="552px"
      style="border-radius: 20px"
      width="100%"
    >
      <v-card-text :ripple="false" class="mx-auto pa-0">
        <v-btn
          v-if="!MANDATORY_STEPS.includes(signupStep)"
          class="close-button-white"
          data-test-id="close-button"
          icon
          @click="closeModal(true)"
        >
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>

        <div class="text-h5 white--text font-weight-600 text-center mb-3">
          <span v-if="signupStep === SIGN_UP_STEPS.EMAIL_VERIFICATION" data-test-id="email-verification-title">
            Email Verification
          </span>
          <span
            v-else-if="signupStep === SIGN_UP_STEPS.VERIFY_PHONE || signupStep === SIGN_UP_STEPS.OTP_VERIFICATION"
            class="font-size-20"
            data-test-id="verify-your-phone-text"
          >
            Verify your phone and get 10,000 QC
          </span>
          <span v-if="signupStep === SIGN_UP_STEPS.CONGRATS">Congratulations!</span>
          <span
            v-else-if="signupStep === SIGN_UP_STEPS.CREDENTIALS || signupStep === SIGN_UP_STEPS.PERSONAL_INFO"
            data-test-id="sign-up-title"
          >
            {{ isSocialSignUp ? 'Your email address has been successfully verified!' : 'Sign Up' }}
          </span>
        </div>

        <!--        Signup Stepper-->
        <v-stepper :value="roundedSignupStep" class="px-1 px-sm-15 ml-2 ml-sm-0 stepper" flat>
          <v-stepper-header>
            <v-stepper-step :complete="roundedSignupStep > 1" :step="1" />
            <v-divider class="step-divider" />
            <v-stepper-step :complete="roundedSignupStep > 2" :step="2" />
            <v-divider class="step-divider" />
            <v-stepper-step :complete="roundedSignupStep > 3" :step="3" class="gift-icon"></v-stepper-step>
          </v-stepper-header>
        </v-stepper>

        <v-slide-x-transition hide-on-leave>
          <div v-if="signupStep === SIGN_UP_STEPS.CREDENTIALS" :key="SIGN_UP_STEPS.CREDENTIALS">
            <sign-up-credentials :model="model" @change="updateCredentials" />
          </div>
          <div v-if="signupStep === SIGN_UP_STEPS.PERSONAL_INFO" :key="SIGN_UP_STEPS.PERSONAL_INFO">
            <signup-personal-info
              :country-list="countryList"
              :model="model"
              :month-list="monthList"
              :states-list="statesList"
            />
          </div>
          <div v-if="signupStep === SIGN_UP_STEPS.EMAIL_VERIFICATION" :key="SIGN_UP_STEPS.EMAIL_VERIFICATION">
            <sign-up-email-verification :email="model.email" />
          </div>
          <div v-if="signupStep === SIGN_UP_STEPS.VERIFY_PHONE" :key="SIGN_UP_STEPS.VERIFY_PHONE">
            <sign-up-verify-phone :model="model" />
          </div>
          <div v-if="signupStep === SIGN_UP_STEPS.OTP_VERIFICATION" :key="SIGN_UP_STEPS.OTP_VERIFICATION">
            <sign-up-o-t-p-verification :phone="model.phone" />
          </div>
          <div v-if="signupStep === SIGN_UP_STEPS.CONGRATS" :key="SIGN_UP_STEPS.CONGRATS">
            <sign-up-congrats />
          </div>
        </v-slide-x-transition>

        <div v-if="MANDATORY_STEPS.includes(signupStep)" class="w-full text-center mt-3">
          <span class="logout-text" data-test-id="sign-out-link" @click="logout">Sign Out</span>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
  .logout-text {
    cursor: pointer;
    color: white;
    text-decoration: underline;
  }

  .v-dialog__content--active {
    overflow-y: auto;
  }

  .outlined-modal-card.confetti {
    position: relative;
    z-index: 1;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 40px;
      background: red;
      display: block;
      padding: 0 40px;
      width: calc(100% - 80px);
      height: 150px;
      background: url('/images/effects/confetti.webp');
      background-repeat: no-repeat;
      pointer-events: none;
    }
  }

  .v-stepper.stepper {
    background: transparent;
    padding: 0 !important;

    &::v-deep {
      .v-stepper__step.gift-icon {
        .v-stepper__step__step {
          font-size: 0;

          &::after {
            content: '';
            position: absolute;
            background-image: url('/images/icons/gift.svg');
            background-size: contain;
            background-repeat: no-repeat;
            width: 18px;
            height: 20px;
          }
        }

        &.v-stepper__step--active,
        &.v-stepper__step--complete {
          .v-stepper__step__step::after {
            filter: invert(1);
          }
        }
      }

      .v-stepper__step__step {
        color: #1c0630 !important;
        width: 45px;
        height: 45px;
        padding: 0;
        font-size: 12px;
        font-weight: 700;
        margin: 0;

        i.v-icon {
          color: #1c0630 !important;
        }
      }

      .v-stepper__step--active {
        .v-stepper__step__step {
          background: white !important;
        }
      }

      .v-stepper__step--complete {
        .v-stepper__step__step {
          background: var(--v-gold-base) !important;
        }
      }

      .v-stepper__step--inactive {
        .v-stepper__step__step {
          background: rgba(255, 255, 255, 0.1) !important;
          color: white !important;
        }
      }

      .v-stepper__step {
        &:first-of-type {
          padding-left: 0;
        }

        &:last-of-type {
          padding-right: 0;
        }
      }

      .step-divider {
        border-color: rgba(255, 255, 255, 0.4) !important;
        margin: 0;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          right: -4px;
          border: solid rgba(255, 255, 255, 0.4);
          border-width: 0 1px 1px 0;
          display: inline-block;
          padding: 2px; // arrow size configured here
          transform: rotate(-45deg) translateY(-4px);
        }
      }
    }
  }

  @media only screen and (max-height: 800px) {
    .v-dialog__content {
      overflow: auto;
    }
  }

  @media only screen and (max-width: 600px) {
    .v-card {
      min-height: 100%;
      width: 100% !important;
      border-radius: 0 !important;
    }
  }
</style>
